import { Constants } from "appConstants";
import { IcoChevronLeft } from "assets/icons";
import { Button, Input } from "components";
import { EulaSections } from "components/EulaModal";
import { cnpj, cpf } from "cpf-cnpj-validator";
import { useFormik } from "formik";
import { ErrorHelper, MaskHelper } from "helpers";
import { UserInfo } from "models/auth/UserInfo";
import { ReactNode, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { UserInfoService } from "services/userInfo";
import { RootState } from "store";
import { setLoggedIn } from "store/features/auth/slice";
import * as Yup from "yup";

import { Tooltip } from "react-tooltip";
import "./styles.scss";

function EulaButton({
	children,
	onClick
}: Readonly<{
	children: ReactNode;
	onClick: () => void;
}>): JSX.Element {
	return (
		<button
			className="p-0 border-0 underline inline text-wrap"
			onClick={(evt) => {
				evt.preventDefault();
				evt.stopPropagation();
				onClick();
			}}
		>
			{children}
		</button>
	);
}

export default function SignUpForm({
	isCompany,
	onGoBack,
	setSelectedEulaSection,
	hideGoBack
}: Readonly<{
	isCompany: boolean;
	onGoBack: () => void;
	setSelectedEulaSection: (newSelectedSection: EulaSections) => void;
	hideGoBack: boolean;
}>) {
	let { user } = useSelector((state: RootState) => state.auth);
	const userInfoService = new UserInfoService();
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const initialValues = useMemo(
		() => ({
			name: user?.name || "",
			number: user?.contactNumber ? MaskHelper.phone(user?.contactNumber.toString()) || "" : "",
			document: user?.document ? (isCompany ? MaskHelper.cnpj(user?.document) || "" : MaskHelper.cpf(user?.document)) || "" : ""
		}),
		[user]
	);

	const hasCompleteInformation = useMemo(() => !!(user?.name && user?.document && user?.contactNumber), [user]);

	const minContactNumber = 10;

	const validationSchema = Yup.object().shape({
		name: Yup.string().required("Campo obrigatório"),
		number: Yup.string().required("Campo obrigatório").min(minContactNumber, "Telefone inválido"),
		document: Yup.string()
			.required("Campo obrigatório")
			.test("document-valid", `${isCompany ? "CNPJ" : "CPF"} inválido`, (value) => {
				if (isCompany) {
					return cnpj.isValid(value || "");
				}
				return cpf.isValid(value || "");
			})
	});

	const [isLoading, setIsLoading] = useState<boolean>(false);
	const formik = useFormik({
		initialValues,
		validationSchema,
		onSubmit: (data) => {
			setIsLoading(true);
			const { email, userId } = user || { email: "", userId: "" };
			const documentType: Constants.UserDocumentType = isCompany ? Constants.UserDocumentType.CNPJ : Constants.UserDocumentType.CPF;
			const contactNumber = Number(data.number.replace(/[^0-9]/g, ""));
			const userInfo = new UserInfo({
				userId,
				email,
				documentType,
				contactNumber,
				acceptedTerms: true,
				...data
			});
			userInfoService
				.createUserInfo(userInfo)
				.then((response) => {
					toast.success("Informações preenchidas!");
					const responseData = response.data;
					const { userName, ...rest } = responseData;
					user = { name: userName, ...rest };

					dispatch(setLoggedIn({ user }));
					navigate("/");
				})
				.catch((error) => {
					toast.error(ErrorHelper.getResponseErrorMessage(error));
				})
				.finally(() => setIsLoading(false));
		}
	});

	return (
		<div className="signup-container">
			<div className="signup-form-container">
				<div className="signup-form-section">
					<div className="font-semibold text-[1.75rem] flex items-center">
						{!hideGoBack && !user?.document && (
							<Button
								kind="icon"
								hierarchy="ghost"
								className="cursor-pointer mr-2 p-1"
								onClick={() => {
									formik.resetForm();
									onGoBack();
								}}
							>
								<IcoChevronLeft size="28" />
							</Button>
						)}
						Portal de Isentos
					</div>
					<p>Solicite a isenção do seu veículo de forma simplificada e acompanhe o andamento do processo.</p>
					<p className="text-[#707275] text-sm">
						Verifique se seu veículo está apto à isenção na concessão escolhida e tenha documentos importantes em mãos ao iniciar o
						processo.
					</p>
				</div>
				{!hasCompleteInformation && (
					<div className="signup-form-section">
						<p className="font-semibold text-base">Revise seus dados para acessar a plataforma</p>
						<Input
							label={isCompany ? "Razão Social" : "Nome e sobrenome"}
							placeholder={isCompany ? "Ex.: CR Engenharia LTDA" : "Ex.: José Maria"}
							value={formik.values.name}
							onChange={formik.handleChange}
							name="name"
							disabled={!!user?.name}
						/>
						<Input
							label={isCompany ? "CNPJ" : "CPF"}
							placeholder={isCompany ? "00.000.000/0000.00" : "000.000.000-00"}
							value={formik.values.document}
							error={formik.errors.document}
							touched={formik.touched.document}
							onChange={(e) => {
								const maskMethod = isCompany ? MaskHelper.cnpj : MaskHelper.cpf;
								formik.setFieldValue("document", maskMethod(e.target.value));
							}}
							name="document"
							disabled={!!user?.document}
						/>
						<Input label="Email" disabled value={user?.email ?? ""} />
						<Input
							label="Telefone"
							placeholder="(00) 00000-0000"
							value={formik.values.number}
							onChange={(e) => formik.setFieldValue("number", MaskHelper.phone(e.target.value))}
							error={formik.errors.number}
							touched={formik.touched.number}
							name="number"
							disabled={!!user?.contactNumber}
						/>
					</div>
				)}
				<div className="signup-form-section">
					<div className="flex flex-col justify-between w-full">
						<p className="text-sm">
							{"Ao prosseguir, você aceita os "}
							<EulaButton onClick={() => setSelectedEulaSection(EulaSections.TERMS)}>Termos e Condições de Uso</EulaButton>
							{", a "}
							<EulaButton onClick={() => setSelectedEulaSection(EulaSections.PRIVACY)}>Política de Privacidade</EulaButton>
							{" e a "}
							<EulaButton onClick={() => setSelectedEulaSection(EulaSections.COOKIES)}>Política de Cookies</EulaButton>
							{" da Motiva."}
						</p>
					</div>
					<Button
						hierarchy="primary"
						className="mt-2 finish-registration"
						onClick={formik.handleSubmit}
						isLoading={isLoading}
						disabled={!formik.isValid}
					>
						Acessar plataforma
					</Button>
					{!formik.isValid && (
						<Tooltip anchorSelect=".finish-registration" place="top">
							<p>Necessário preencher todos os campos</p>
							<p>com informações válidas para prosseguir</p>
						</Tooltip>
					)}
				</div>
			</div>
		</div>
	);
}
